import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Flame, Diamond, Heart, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePoints } from '@/context/PointsContext';

export default function StatsBar({ totalXP = null, compact = false }) {
  const { points, loading } = usePoints();
  const [animateXP, setAnimateXP] = useState(false);
  const [prevXP, setPrevXP] = useState(0);

  // Use real data from PointsContext if available, otherwise use props/defaults
  // Only use defaults if not loading and points is null (to avoid showing 0 while loading)
  const xp = totalXP !== null ? totalXP : (points?.total_xp ?? (loading ? null : 0));
  const gems = points?.gems ?? (loading ? null : 0);
  const hearts = points?.hearts ?? (loading ? null : 5);
  const streak = points?.current_streak ?? (loading ? null : 0);

  // Trigger animation when XP changes
  useEffect(() => {
    if (xp > prevXP && prevXP > 0) {
      setAnimateXP(true);
      const timer = setTimeout(() => {
        setAnimateXP(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    setPrevXP(xp);
  }, [xp, prevXP]);

  // Order: 1. XP (with trophy icon and constant animation), 2. Diamond (Gems), 3. Flame (Streaks), 4. Heart (Hearts)
  const stats = [
    { icon: Trophy, value: xp, label: 'XP', gradient: 'from-yellow-400 via-yellow-500 to-amber-600', shine: true, animate: true, isXP: true, iconSize: compact ? 'w-4 h-4 sm:w-5 sm:h-5' : 'w-4 h-4 sm:w-5 sm:h-5' },
    { icon: Diamond, value: gems, label: 'Gems', gradient: 'from-purple-400 via-fuchsia-500 to-pink-500', shine: false, animate: false, isXP: false, iconSize: compact ? 'w-3 h-3 sm:w-4 sm:h-4' : 'w-3.5 h-3.5 sm:w-4 h-4' },
    { icon: Flame, value: streak, label: 'Day Streak', gradient: 'from-orange-400 via-orange-500 to-red-500', shine: false, animate: false, isXP: false, iconSize: compact ? 'w-3 h-3 sm:w-4 sm:h-4' : 'w-3.5 h-3.5 sm:w-4 h-4' },
    { icon: Heart, value: hearts, label: 'Hearts', gradient: 'from-pink-400 via-rose-500 to-rose-600', shine: false, animate: false, isXP: false, iconSize: compact ? 'w-3 h-3 sm:w-4 sm:h-4' : 'w-3.5 h-3.5 sm:w-4 h-4' },
  ];

  const contentClass = compact
    ? 'px-2 py-1.5 sm:px-3 sm:py-2'
    : 'px-3 py-2 sm:px-4 sm:py-3';

  return (
    <Card className="bg-gradient-to-r from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))]/80 border-[hsl(var(--card-border))] overflow-hidden shadow-lg" data-stats-bar>
      <CardContent className={contentClass}>
        {loading && !points ? (
          <div className="flex items-center justify-center py-2">
            <div className="w-4 h-4 border-2 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center sm:justify-between gap-2 sm:gap-3">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isXP = stat.isXP || false;
              // Responsive dimensions based on compact mode
              const xpDim = compact ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-9 h-9 sm:w-11 sm:h-11';
              const normalDim = compact ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-7 h-7 sm:w-9 sm:h-9';

              return (
                <div
                  key={index}
                  className="relative group cursor-pointer flex items-center gap-2"
                >
                  {/* Icon with gradient background - larger size for XP */}
                  <div className={`relative ${isXP ? xpDim : normalDim} rounded-full bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 ${isXP ? 'animate-pulse' : ''}`} style={isXP ? { animationDuration: '1s' } : {}}>
                    <Icon className={`${stat.iconSize} text-white drop-shadow-lg ${isXP ? 'animate-bounce' : ''}`} style={isXP ? { animationDuration: '1s', animationIterationCount: 'infinite' } : {}} />

                    {/* Constant sparkle effect for XP icon */}
                    {isXP && (
                      <>
                        <Sparkles className="absolute -top-0.5 -right-0.5 w-3 h-3 text-yellow-300 animate-ping" style={{ animationDuration: '3s' }} />
                        <Sparkles className="absolute -bottom-0.5 -left-0.5 w-2.5 h-2.5 text-amber-300 animate-ping" style={{ animationDelay: '1.5s', animationDuration: '3s' }} />
                        <Sparkles className="absolute top-1/2 -left-1 w-2 h-2 text-yellow-200 animate-pulse" style={{ animationDelay: '0.8s', animationDuration: '1s' }} />
                      </>
                    )}

                    {/* Rotating glow ring for XP */}
                    {isXP && (
                      <div className="absolute inset-0 rounded-full border-2 border-yellow-300/50 animate-spin" style={{ animationDuration: '5s' }} />
                    )}

                    {/* Inner glow on hover */}
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-40 blur-sm transition-opacity duration-300`} />
                  </div>

                  {/* Value only (no label) */}
                  <div className={`text-base sm:text-lg font-bold text-foreground drop-shadow-sm transition-all duration-300`}>
                    {stat.value ?? '-'}
                  </div>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-[hsl(var(--teal-vivid))]/10 to-[hsl(var(--green-bright))]/10 -z-10 blur-xl" />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
